import React, { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Building, 
  Briefcase, 
  Handshake, 
  Users, 
  Bell, 
  Calendar, 
  Clock, 
  ChevronRight, 
  FileCheck,
  CheckCircle,
  Circle,
  BarChart,
  TrendingUp,
  Layers,
  Search,
  Filter,
  Download,
  Moon,
  Sun,
  ChevronDown,
  Star,
  ArrowUpDown
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const Client = () => {
  const { toast } = useToast();
  const [activeNotifications, setActiveNotifications] = useState(3);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [projectStatusFilter, setProjectStatusFilter] = useState("all");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState("date"); // "date", "budget", "completion"

  React.useEffect(() => {
    toast({
      title: "Welcome to your Client Dashboard",
      description: "Manage your projects and find the perfect talent for your needs.",
      variant: "success",
    });
    
    // Apply dark mode from user preferences if saved
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleDarkMode = useCallback(() => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem("darkMode", String(newMode));
    
    if (newMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const clientStats = [
    {
      title: "Active Projects",
      value: "12",
      description: "Currently in progress",
      icon: <Briefcase className="w-5 h-5 text-emerald-500" />,
      trend: "+2 this month",
      trendUp: true,
    },
    {
      title: "Total Hires",
      value: "48",
      description: "Successful collaborations",
      icon: <Handshake className="w-5 h-5 text-sky-500" />,
      trend: "+5 this month",
      trendUp: true,
    },
    {
      title: "Team Size",
      value: "15",
      description: "Active freelancers",
      icon: <Users className="w-5 h-5 text-purple-500" />,
      trend: "+3 this month",
      trendUp: true,
    },
    {
      title: "Departments",
      value: "4",
      description: "Project categories",
      icon: <Building className="w-5 h-5 text-indigo-500" />,
      trend: "No change",
      trendUp: null,
    },
  ];

  const recentProjects = [
    {
      title: "Web3 DApp Development",
      status: "In Progress",
      freelancer: "Alex Thompson",
      completion: 75,
      budget: "$8,500",
      nextMilestone: "Smart Contract Deployment",
      deadline: "May 15, 2024",
      tags: ["blockchain", "web3", "smart-contract"],
      date: "2024-04-01",
    },
    {
      title: "UI/UX Redesign",
      status: "Completed",
      freelancer: "Sarah Chen",
      completion: 100,
      budget: "$5,200",
      nextMilestone: "Final Delivery",
      deadline: "Completed",
      tags: ["design", "ui", "ux"],
      date: "2024-03-15",
    },
    {
      title: "Smart Contract Audit",
      status: "Planning",
      freelancer: "Michael Roberts",
      completion: 10,
      budget: "$12,000",
      nextMilestone: "Initial Setup",
      deadline: "May 28, 2024",
      tags: ["blockchain", "audit", "security"],
      date: "2024-04-10",
    },
    {
      title: "Mobile App Development",
      status: "In Progress",
      freelancer: "Jessica Wang",
      completion: 45,
      budget: "$15,000",
      nextMilestone: "Beta Testing",
      deadline: "June 10, 2024",
      tags: ["mobile", "app", "react-native"],
      date: "2024-03-25",
    },
  ];

  const upcomingMeetings = [
    {
      title: "Project Kickoff: NFT Marketplace",
      with: "DevTeam Alpha",
      time: "Today, 3:00 PM",
      duration: "45 min",
      type: "Video Call",
      agenda: ["Project scope review", "Team introductions", "Timeline planning"],
      link: "https://meet.example.com/abc123"
    },
    {
      title: "Weekly Progress Review",
      with: "Sarah Chen, UI/UX Lead",
      time: "Tomorrow, 10:00 AM",
      duration: "30 min",
      type: "Video Call",
      agenda: ["Review completed designs", "Discuss feedback", "Plan next sprint"],
      link: "https://meet.example.com/def456"
    },
    {
      title: "Smart Contract Discussion",
      with: "Michael Roberts, Blockchain Dev",
      time: "May 10, 2:15 PM",
      duration: "60 min",
      type: "In Person",
      agenda: ["Security considerations", "Gas optimization", "Testing strategy"],
      location: "Office - Conference Room A"
    },
  ];

  const notifications = [
    {
      id: 1,
      title: "New proposal received",
      description: "Alex Thompson submitted a proposal for your DApp project",
      time: "10 minutes ago",
      unread: true,
    },
    {
      id: 2,
      title: "Milestone completed",
      description: "UI/UX Redesign: Frontend implementation milestone completed",
      time: "2 hours ago",
      unread: true,
    },
    {
      id: 3,
      title: "Contract ready for signature",
      description: "Smart Contract Audit agreement is ready for your review",
      time: "3 hours ago",
      unread: true,
    },
    {
      id: 4,
      title: "Invoice paid",
      description: "Payment for UI/UX Redesign has been processed",
      time: "Yesterday",
      unread: false,
    },
    {
      id: 5,
      title: "New message",
      description: "You have a new message from Michael Roberts",
      time: "Yesterday",
      unread: false,
    },
  ];

  const projectTimeline = [
    {
      title: "Project Brief",
      description: "Initial document sharing",
      date: "April 5, 2024",
      status: "Completed",
    },
    {
      title: "Talent Selection",
      description: "Interview and selection process",
      date: "April 10, 2024",
      status: "Completed",
    },
    {
      title: "Development Phase 1",
      description: "Backend infrastructure setup",
      date: "April 20, 2024",
      status: "Completed",
    },
    {
      title: "Development Phase 2",
      description: "Frontend implementation",
      date: "May 5, 2024",
      status: "Current",
    },
    {
      title: "Testing",
      description: "Quality assurance and testing",
      date: "May 20, 2024",
      status: "Upcoming",
    },
    {
      title: "Deployment",
      description: "Production deployment",
      date: "June 5, 2024",
      status: "Upcoming",
    },
  ];

  const handleMarkAllRead = useCallback(() => {
    setActiveNotifications(0);
    setShowNotifications(false);
    toast({
      title: "All notifications marked as read",
      variant: "success",
    });
  }, [toast]);

  const handleNotificationClick = useCallback(() => {
    setShowNotifications(!showNotifications);
  }, [showNotifications]);
  
  const handleExportData = useCallback(() => {
    setIsLoading(true);
    
    // Simulate export process
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: "Data exported successfully",
        description: "Your project data has been exported to CSV.",
        variant: "success",
      });
    }, 1500);
  }, [toast]);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = [...recentProjects];
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(project => 
        project.title.toLowerCase().includes(term) || 
        project.freelancer.toLowerCase().includes(term) ||
        project.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (projectStatusFilter !== "all") {
      filtered = filtered.filter(project => 
        project.status.toLowerCase() === projectStatusFilter.toLowerCase()
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "budget") {
        return parseFloat(a.budget.replace("$", "").replace(",", "")) - 
               parseFloat(b.budget.replace("$", "").replace(",", ""));
      } else if (sortBy === "completion") {
        return a.completion - b.completion;
      } else {
        // Default: sort by date
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });
    
    return filtered;
  }, [recentProjects, searchTerm, projectStatusFilter, sortBy]);
  
  // Create favorite project functionality
  const [favoriteProjects, setFavoriteProjects] = useState([]);
  
  const toggleFavorite = useCallback((title) => {
    setFavoriteProjects(prev => 
      prev.includes(title) 
        ? prev.filter(p => p !== title) 
        : [...prev, title]
    );
  }, []);

  return (
    <div className={`container mx-auto py-8 space-y-8 transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div className="flex justify-between items-center mb-8 animate-fade-in">
        <div>
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
            Client Dashboard
          </h1>
          <p className={`mt-2 text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Manage your projects and talent pool effectively
          </p>
        </div>
        <div className="flex gap-4 items-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="rounded-full"
                  onClick={toggleDarkMode}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Toggle {isDarkMode ? 'Light' : 'Dark'} Mode</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <div className="relative">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="relative rounded-full"
                    onClick={handleNotificationClick}
                  >
                    <Bell className="w-5 h-5" />
                    {activeNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {activeNotifications}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{activeNotifications} unread notifications</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {showNotifications && (
              <Card className={`absolute right-0 mt-2 w-80 z-50 shadow-xl animate-fade-in ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-100'}`}>
                <CardHeader className="p-4 pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">Notifications</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleMarkAllRead}
                      className="text-xs h-8"
                    >
                      Mark all read
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`p-4 border-b last:border-b-0 cursor-pointer transition-colors ${
                          isDarkMode
                            ? notification.unread ? 'bg-blue-900/20 hover:bg-blue-900/30' : 'hover:bg-gray-700'
                            : notification.unread ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`w-2 self-stretch rounded-full ${notification.unread ? 'bg-blue-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
                          <div>
                            <h4 className="font-medium">{notification.title}</h4>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{notification.description}</p>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{notification.time}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <Button 
            className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Post New Project
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {clientStats.map((stat, index) => (
          <Card 
            key={stat.title} 
            className={`hover:shadow-xl transition-all duration-300 backdrop-blur-sm border-blue-100 animate-fade-in ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white/50'}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl shadow-sm ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800' : 'bg-gradient-to-br from-sky-50 to-emerald-50'}`}>
                  {stat.icon}
                </div>
                <span className="text-3xl font-bold">{stat.value}</span>
              </div>
              <h3 className="font-semibold text-lg">{stat.title}</h3>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{stat.description}</p>
              
              {stat.trend && (
                <div className="mt-3 flex items-center">
                  {stat.trendUp === true && <TrendingUp className="w-3 h-3 text-emerald-500 mr-1" />}
                  {stat.trendUp === false && <TrendingUp className="w-3 h-3 text-red-500 mr-1 rotate-180" />}
                  <span className={`text-xs font-medium ${
                    stat.trendUp === true ? 'text-emerald-500' : 
                    stat.trendUp === false ? 'text-red-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {stat.trend}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="projects" className="w-full mt-8">
        <TabsList className={`grid grid-cols-3 w-full max-w-md mx-auto mb-8 ${isDarkMode ? 'bg-gray-800' : ''}`}>
          <TabsTrigger value="projects" className="text-sm">
            <Layers className="w-4 h-4 mr-2" />
            Projects
          </TabsTrigger>
          <TabsTrigger value="timeline" className="text-sm">
            <Clock className="w-4 h-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="meetings" className="text-sm">
            <Calendar className="w-4 h-4 mr-2" />
            Meetings
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="projects" className="animate-fade-in">
          {/* Projects Filter Bar */}
          <div className={`flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input 
                placeholder="Search projects or freelancers..." 
                className={`pl-10 ${isDarkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <Select value={projectStatusFilter} onValueChange={setProjectStatusFilter}>
                <SelectTrigger className={`w-[180px] ${isDarkMode ? 'bg-gray-700 border-gray-600' : ''}`}>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="in progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                </SelectContent>
              </Select>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className={`${isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : ''}`}>
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setSortBy("date")}>
                    Date {sortBy === "date" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("budget")}>
                    Budget {sortBy === "budget" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("completion")}>
                    Completion {sortBy === "completion" && <CheckCircle className="w-4 h-4 ml-2" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`${isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : ''}`}
                      onClick={handleExportData}
                      disabled={isLoading}
                    >
                      {isLoading ? 
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" /> :
                        <Download className="w-4 h-4 mr-2" />
                      }
                      Export
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export projects data as CSV</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Card className={`xl:col-span-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} animate-fade-in`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
                  Projects
                </CardTitle>
                <Badge variant="outline" className={isDarkMode ? 'border-gray-600' : ''}>
                  {filteredProjects.length} projects
                </Badge>
              </CardHeader>
              <CardContent>
                {filteredProjects.length === 0 ? (
                  <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <h3 className="text-lg font-medium mb-1">No projects found</h3>
                    <p>Try changing your search or filter criteria</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredProjects.map((project, index) => (
                      <div 
                        key={project.title}
                        className={`p-4 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md ${
                          isDarkMode 
                            ? 'bg-gradient-to-r from-sky-900/30 to-emerald-900/30 hover:from-sky-900/40 hover:to-emerald-900/40' 
                            : 'bg-gradient-to-r from-sky-50/50 to-emerald-50/50 hover:from-sky-100/50 hover:to-emerald-100/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h4 className="text-lg font-semibold">{project.title}</h4>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6" 
                                onClick={() => toggleFavorite(project.title)}
                              >
                                <Star 
                                  className={`w-4 h-4 ${
                                    favoriteProjects.includes(project.title) 
                                      ? 'text-yellow-500 fill-yellow-500' 
                                      : isDarkMode ? 'text-gray-600' : 'text-gray-300'
                                  }`} 
                                />
                              </Button>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {project.freelancer}
                              </span>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4" />
                                {project.budget}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {project.tags.map(tag => (
                                <Badge 
                                  key={tag} 
                                  variant="secondary" 
                                  className={`text-xs py-0 ${
                                    isDarkMode 
                                      ? 'bg-gray-700 hover:bg-gray-600' 
                                      : 'bg-gray-100 hover:bg-gray-200'
                                  }`}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium mb-1">{project.completion}</div>
                            <Badge 
                              variant="secondary"
                              className={`px-3 py-1 ${
                                project.status === "Completed" 
                                  ? isDarkMode ? "bg-emerald-900/50 text-emerald-300 border-emerald-800" : "bg-emerald-100 text-emerald-700 border-emerald-200"
                                  : project.status === "In Progress"
                                  ? isDarkMode ? "bg-sky-900/50 text-sky-300 border-sky-800" : "bg-sky-100 text-sky-700 border-sky-200"
                                  : isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-700 border-gray-200"
                              }`}
                            >
                              {project.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              project.status === "Completed"
                                ? "bg-emerald-500"
                                : "bg-sky-500"
                            }`}
                            style={{ width: project.completion }}
                          />
                        </div>
                        
                        <div className="flex justify-between mt-4 text-sm">
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                            <FileCheck className="w-4 h-4" />
                            <span>Next: {project.nextMilestone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                            <Clock className="w-4 h-4" />
                            <span>Due: {project.deadline}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              {filteredProjects.length > 0 && (
                <CardFooter className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} px-6 py-4`}>
                  <div className="flex justify-between items-center w-full">
                    <Button variant="ghost" size="sm" disabled>
                      Page 1 of 1
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled>Previous</Button>
                      <Button variant="outline" size="sm" disabled>Next</Button>
                    </div>
                  </div>
                </CardFooter>
              )}
            </Card>

            <div className="space-y-6">
              <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} animate-fade-in`}>
                <CardHeader>
                  <CardTitle className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
                    Hiring Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-sky-900/30 to-emerald-900/30 hover:shadow-md' 
                        : 'bg-gradient-to-r from-sky-50/50 to-emerald-50/50 hover:shadow-md'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-sky-900/50' : 'bg-sky-100'}`}>
                          <Users className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
                        </div>
                        <div>
                          <div className="font-medium">Active Proposals</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>15 new proposals today</div>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className={isDarkMode ? 'hover:bg-sky-900/50 border-gray-700' : 'hover:bg-sky-50'}
                      >
                        Review
                      </Button>
                    </div>

                    <div className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-sky-900/30 to-emerald-900/30 hover:shadow-md' 
                        : 'bg-gradient-to-r from-sky-50/50 to-emerald-50/50 hover:shadow-md'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                          <Handshake className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        </div>
                        <div>
                          <div className="font-medium">Pending Contracts</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>3 contracts awaiting signature</div>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className={isDarkMode ? 'hover:bg-emerald-900/50 border-gray-700' : 'hover:bg-emerald-50'}
                      >
                        Sign
                      </Button>
                    </div>
                    
                    <div className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-sky-900/30 to-emerald-900/30 hover:shadow-md' 
                        : 'bg-gradient-to-r from-sky-50/50 to-emerald-50/50 hover:shadow-md'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                          <BarChart className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <div>
                          <div className="font-medium">Project Analytics</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>View detailed performance</div>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className={isDarkMode ? 'hover:bg-purple-900/50 border-gray-700' : 'hover:bg-purple-50'}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'} animate-fade-in`}>
                <CardHeader>
                  <CardTitle className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <Button 
                      variant="outline" 
                      className={`h-auto py-6 flex flex-col items-center gap-3 ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 hover:bg-gradient-to-r hover:from-sky-900/20 hover:to-emerald-900/20' 
                          : 'hover:bg-gradient-to-r hover:from-sky-50 hover:to-emerald-50'
                      } transition-all duration-300`}
                    >
                      <Users className="w-6 h-6 text-sky-500" />
                      <span>Browse Talent</span>
                    </Button>
                    <Button 
                      variant="outline"
                      className={`h-auto py-6 flex flex-col items-center gap-3 ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 hover:bg-gradient-to-r hover:from-sky-900/20 hover:to-emerald-900/20' 
                          : 'hover:bg-gradient-to-r hover:from-sky-50 hover:to-emerald-50'
                      } transition-all duration-300`}
                    >
                      <Briefcase className="w-6 h-6 text-emerald-500" />
                      <span>Post Project</span>
                    </Button>
                    <Button 
                      variant="outline"
                      className={`h-auto py-6 flex flex-col items-center gap-3 ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 hover:bg-gradient-to-r hover:from-sky-900/20 hover:to-emerald-900/20' 
                          : 'hover:bg-gradient-to-r hover:from-sky-50 hover:to-emerald-50'
                      } transition-all duration-300`}
                    >
                      <Building className="w-6 h-6 text-purple-500" />
                      <span>Team Access</span>
                    </Button>
                    <Button 
                      variant="outline"
                      className={`h-auto py-6 flex flex-col items-center gap-3 ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 hover:bg-gradient-to-r hover:from-sky-900/20 hover:to-emerald-900/20' 
                          : 'hover:bg-gradient-to-r hover:from-sky-50 hover:to-emerald-50'
                      } transition-all duration-300`}
                    >
                      <Handshake className="w-6 h-6 text-indigo-500" />
                      <span>Contracts</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="timeline" className="animate-fade-in">
          <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'}`}>
            <CardHeader>
              <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
                Project Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className={`absolute top-0 bottom-0 left-3 w-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                <div className="space-y-8">
                  {projectTimeline.map((phase, index) => (
                    <div key={index} className="flex gap-4 relative">
                      <div className="z-10 flex-shrink-0 mt-1">
                        {phase.status === "Completed" ? (
                          <div className={`w-6 h-6 rounded-full ${isDarkMode ? 'bg-emerald-900' : 'bg-emerald-100'} flex items-center justify-center`}>
                            <CheckCircle className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                          </div>
                        ) : phase.status === "Current" ? (
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
                            <Circle className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className={`w-6 h-6 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center`}>
                            <Circle className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          </div>
                        )}
                      </div>
                      <div className={`bg-gradient-to-r p-4 rounded-lg w-full ${
                        phase.status === "Completed" 
                          ? isDarkMode ? "from-emerald-900/30 to-emerald-900/20 border border-emerald-900/50" : "from-emerald-50 to-emerald-100/50 border border-emerald-100"
                          : phase.status === "Current"
                          ? isDarkMode ? "from-blue-900/30 to-blue-900/20 border border-blue-900/50" : "from-blue-50 to-blue-100/50 border border-blue-100"
                          : isDarkMode ? "from-gray-800 to-gray-800 border border-gray-700" : "from-gray-50 to-gray-100/50 border border-gray-100"
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">{phase.title}</h4>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{phase.description}</p>
                          </div>
                          <Badge 
                            variant="secondary"
                            className={`${
                              phase.status === "Completed" 
                                ? isDarkMode ? "bg-emerald-900/50 text-emerald-300" : "bg-emerald-100 text-emerald-700"
                                : phase.status === "Current"
                                ? isDarkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700"
                                : isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {phase.status}
                          </Badge>
                        </div>
                        <div className={`mt-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{phase.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="meetings" className="animate-fade-in">
          <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'}`}>
            <CardHeader>
              <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-600">
                Upcoming Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {upcomingMeetings.map((meeting, index) => (
                  <Card key={index} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'border border-blue-100'} hover:shadow-md transition-all`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                          <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`${
                            meeting.type === "Video Call" 
                              ? isDarkMode ? "border-blue-800 bg-blue-900/30 text-blue-300" : "border-blue-200 bg-blue-50 text-blue-700" 
                              : isDarkMode ? "border-purple-800 bg-purple-900/30 text-purple-300" : "border-purple-200 bg-purple-50 text-purple-700"
                          }`}
                        >
                          {meeting.type}
                        </Badge>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="font-semibold">{meeting.title}</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>{meeting.with}</p>
                      </div>
                      
                      <div className="mt-4 flex items-center gap-6">
                        <div className="flex items-center gap-1.5">
                          <Clock className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{meeting.time}</span>
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {meeting.duration}
                        </div>
                      </div>
                      
                      {/* New: Meeting agenda */}
                      <div className={`mt-3 p-2 rounded-md text-sm ${isDarkMode ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                        <div className="font-medium mb-1">Agenda:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {meeting.agenda.map((item, i) => (
                            <li key={i} className="text-xs">{item}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <Button 
                        className={`w-full mt-4 ${
                          isDarkMode
                            ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                            : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                        }`}
                      >
                        Join Meeting
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Client;
